import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { AuthProvider, useAuth } from './auth/useAuth'
import { SignIn } from './pages/SignIn'
import { Workspace } from './pages/Workspace'
import { Engagement } from './pages/Engagement'
import { ErrorBoundary } from './components/chrome/ErrorBoundary'
import './styles/eia-brand.css'

// THE THREE VIEWS' OWN STYLESHEETS, lifted from `Frontend/*.html`. Each scopes its layout rules to
// `body[data-view="…"]`, which the pages stamp, so one document can carry all three without the
// engagement's `overflow:hidden` reaching the workspace.
import './styles/signin.css'
import './styles/workspace.css'
import './styles/engagement.css'

/**
 * Everything but `/signin` needs a signed-in, entitled principal.
 *
 * <p><b>THE SIGN-IN PAGE IS RENDERED IN PLACE, NOT NAVIGATED TO</b>, and that is the whole of the
 * deep-link guarantee. Navigating first replaced the URL with `/signin` BEFORE the redirect began,
 * so MSAL recorded `/signin` as the login request URL and `navigateToLoginRequestUrl` faithfully
 * brought the user back to it — signed in, looking at a sign-in page, with the page they had asked
 * for gone. Reported from the deployed site as "upon signin, user is returned back to signin page;
 * takes another 3 or 4 clicks".</p>
 *
 * <p>Rendering in place leaves the URL as the one they asked for, so the round trip returns them to
 * it and this guard then lets them through. A gate notification lands on its gate.</p>
 */
function Guarded({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { state } = useAuth()

  if (state === 'loading') return <main className="loading" aria-busy="true" />
  if (state !== 'signed-in') return <SignIn />
  return <>{children}</>
}

/** `/signin` visited directly. Someone already signed in has no business staying on it. */
function SignInRoute(): React.JSX.Element {
  const { state } = useAuth()

  if (state === 'loading') return <main className="loading" aria-busy="true" />
  if (state === 'signed-in') return <Navigate to="/" replace />
  return <SignIn />
}

function App(): React.JSX.Element {
  return (
    <Routes>
      <Route path="/signin" element={<SignInRoute />} />
      <Route path="/" element={<Guarded><Workspace /></Guarded>} />
      <Route path="/e/:engagementId" element={<Guarded><Engagement /></Guarded>} />

      {/* Anything else is the workspace. `navigationFallback` already rewrote the document. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

const root = document.getElementById('root')
if (root) {
  createRoot(root).render(
    <StrictMode>
      {/* R-10: RTL-READY, ENGLISH-ONLY. `dir` on the root and logical properties throughout — no
          translation layer in v1, and no directional assumption to unpick when there is one. */}
      <BrowserRouter>
        <AuthProvider groupObjectId={String(import.meta.env['VITE_AUTH_GROUP_ID'] ?? '')}>
          {/* THE LAST LINE, NOT THE FIRST. Cards carry their own boundary; this one exists so a
              fault anywhere else is a sentence on the page rather than a white screen. */}
          <ErrorBoundary fallback={
            <main className="page" id="main" role="alert">
              <p>This page could not be displayed. Reload to try again, or return to the{' '}
                <a href="/">workspace</a>.</p>
            </main>
          }>
            <App />
          </ErrorBoundary>
        </AuthProvider>
      </BrowserRouter>
    </StrictMode>)
}
